<template>
  <div
    class="k-input "
    :class="{ '-error': error, '-disabled': disabled }"
    @click="!disabled ? handleClick() : ''"
  >
    <label
      v-if="label"
      class="label"
      :for="name"
    >{{ label }}</label>
    <div class="input-container">
      <k-icon
        v-if="error || icon"
        :icon="error ? 'warning1' : icon"
        class="icon"
      />
      <money
        class="input"
        v-if="type === 'money'"
        :value="internalValue"
        v-model="internalValue"
        v-bind="currencyMask"
        ref="money"
      />
      <input
        v-else
        :placeholder="setPlaceholder"
        class="input"
        :type="setType"
        v-model="internalValue"
        ref="input"
        v-mask="mask"
        @blur="touched = true"
        @onfocusout="touched = true"
        :disabled="disabled"
        :autocomplete="setTypeAutocomplete"
        :name="name"
      />
    </div>
    <show-password
      v-if="type === 'password'"
      :show="showPassword"
      class="show-password"
      @click.native="showPassword = !showPassword"
    />
  </div>
</template>

<script>
import { mask } from 'vue-the-mask'
import KIcon from './KIcon.vue'
import { Money } from 'v-money'

export default {
  directives: {
    mask (el, binding) {
      if (!binding.value || !binding.value.length) { return }

      return mask(el, binding)
    }
  },
  components: {
    showPassword: () => import('./ShowPassword.vue'),
    KIcon,
    Money
  },
  props: {
    icon: {
      type: String,
      default: ''
    },
    value: {
      type: [String, Number],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    email: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
    },
    errorMessage: {
      type: String,
      default: 'Erro!'
    },
    mask: {
      type: undefined
    }
  },
  data () {
    return {
      currencyMask: {
        prefix: 'R$ ',
        decimal: ',',
        thousands: '.',
        precision: 2,
        masked: false
      },
      internalValue: '',
      touched: false,
      showPassword: false
    }
  },
  computed: {
    error () {
      if (this.hasError) return this.hasError
      return this.touched && (this.emailValidation || this.requiredValidation)
    },
    errorMsg () {
      if (this.hasError) return this.errorMessage
      else if (this.required) return 'Campo obrigatório!'
      else if (this.email && !this.internalValue) return 'Email inválido!'
      else return ''
    },
    emailValidation () {
      const emailTest = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.internalValue)
      return this.internalValue && this.email && !emailTest
    },
    requiredValidation () {
      return this.required && this.internalValue === ''
    },
    setPlaceholder () {
      if (this.error) return this.errorMsg
      return this.placeholder
    },
    setType () {
      if (this.type === 'password') {
        if (this.showPassword) return 'text'
        return 'password'
      }
      return 'text'
    },
    setTypeAutocomplete () {
      if (this.type === 'password') return 'new-password'
      return 'off'
    }
  },
  watch: {
    internalValue: {
      handler: function () {
        this.$emit('input', this.internalValue)
      },
      deep: true
    },
    disabled () {
      if (this.type === 'money') { this.enableMoneyInput(!this.disabled) }
    }
  },
  created () {
    this.internalValue = JSON.parse(JSON.stringify(this.value))
  },
  mounted () {
    if (this.type === 'money' && this.disabled) { this.enableMoneyInput() }
  },
  methods: {
    enableMoneyInput (enable) {
      let moneyInput = this.$refs.money.$el
      if (enable) { moneyInput.removeAttribute('disabled') }
      else { moneyInput.setAttribute('disabled', '') }
    },
    handleClick () {
      this.touched = false
      if (this.type === 'money') {
        this.$refs.money.$el.focus()
      } else {
        this.$refs.input.focus()
      }
    }
  }

}
</script>

<style lang="scss" scoped>
@import "../assets/scss/main.scss";

.k-input {
  background-color: #ffffff;
  border-radius: 5px;
  box-shadow: lightgrey 0px 1px 3px 0px;
  box-sizing: border-box;
  width: 100%;
  max-width: 508px;
  height: 60px;
  padding: 14px 16px;
  font-family: $font-family;
  position: relative;
  .label {
    height: 11px;
    color: rgba($neutral-1, 0.5);
    font-size: 11px;
    font-weight: 400;
    display: block;
    text-align: left;
    text-transform: uppercase;
  }
  .input-container {
    display: flex;
    align-items: center;
    padding-top: $spacing-xs;
    .icon {
      padding-right: $spacing-xs;
      font-weight: bold;
    }
    .input {
      background: #fff;
      text-align: left;
      margin-right: 10px;
      width: 100%;
      border: none;
      -moz-appearance: textfield;
      box-sizing: border-box;
      font-weight: bold;
      color: rgba($neutral-1, 0.9);
      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      &:focus {
        color: rgba($neutral-1, 0.8);
        outline: none;
      }
    }
  }
  .show-password {
    position: absolute;
    right: 20px;
    top: 32px;
    color: rgba($neutral-1, 0.5);
  }
  &.-error {
    box-shadow: $primary-6 0px 0px 5px 0px;
    .label {
      color: rgba($primary-6, 0.5);
    }
    & ::placeholder,
    .icon,
    .show-password {
      color: $primary-6;
    }
    & ::placeholder {
      font-size: 14px;
    }
  }
  &.-disabled {
    background: $disabled-color-2;
    cursor: not-allowed;
    .label {
      color: rgba(32, 41, 80, 0.4);
    }
    .input {
      background: inherit;
      cursor: not-allowed;
    }
  }
}
</style>



// WEBPACK FOOTER //
// node_modules/kaledo-components/src/components/KInput.vue